import type { SearchParams, SetSearchFn } from '@/hooks/useRouterQuery';
import type { DurationUnit, ToHumanDurationOptions } from 'luxon';
import type React from 'react';
import { TRPCClientError } from '@trpc/client';
import { max, min } from '@poseidon/shared-utils';
import { DateTime, Duration } from 'luxon';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import type ContactInteractionAction from '@poseidon/types/pg-gen/contactInteractionAction';
import type { SvrCadenceStep } from '@poseidon/types/svr/svrCadence.type';
import type { SvrDocument } from '@poseidon/types/svr/svrDocument.type';
import type { SvrHistoryStepType } from '@poseidon/types/svr/svrStep.type';

import { isNotEmpty, isNotNull } from '@harmonizeai/poseidon-shared';

export type EditorDocument =
  | SvrDocument
  | {
      id: string;
      isPrivate: true;
    };

/**
 * Resolve a document's display thumbnail. Prefer the generated OG image; for image
 * files fall back to the file itself, since OG images aren't always generated for
 * uploaded images. Mirrors ContentLibraryCard so the content library and the TipTap
 * `/` dropdown render the same thumbnail.
 */
export const documentThumbnailUrl = (doc: {
  ogImageUrl?: string | null;
  fileUrl?: string | null;
  fileContentType?: string | null;
}): string | undefined =>
  doc.ogImageUrl ?? (doc.fileContentType?.startsWith('image/') ? (doc.fileUrl ?? undefined) : undefined);

export function toHuman(dur: Duration, smallestUnit: DurationUnit = 'seconds', opts?: ToHumanDurationOptions): string {
  const units: DurationUnit[] = ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'];
  const smallestIdx = units.indexOf(smallestUnit);
  const entries = Object.entries(
    dur
      .shiftTo(...units)
      .normalize()
      .toObject(),
  ).filter(([_unit, amount], idx) => amount > 0 && idx <= smallestIdx);
  const dur2 = Duration.fromObject(entries.length === 0 ? { [smallestUnit]: 0 } : Object.fromEntries(entries));
  return dur2.toHuman(opts);
}

export const getStringValue = (maybeString: string | string[] | undefined) => {
  if (Array.isArray(maybeString)) return maybeString.join(',');
  else return maybeString;
};

/**
 * The viewer's live browser IANA timezone (e.g. `America/New_York`), or undefined during SSR / when
 * unavailable. Sent to the call-queue endpoints so the "outside business hours" window is judged in
 * the rep's own local time (see `voiceCall.callQueue`), following wherever they actually are.
 */
export function getBrowserTimezone(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export const getLinkedinUrl = (username: string | undefined, pid: string | undefined) => {
  const url = `https://linkedin.com/in/`;
  if (username != null) {
    return url + username;
  } else if (pid) {
    return url + pid;
  } else {
    return undefined;
  }
};

export const blobToBase64 = (blob: File) => {
  return new Promise<string | undefined>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result == null || typeof reader.result !== 'string') {
        resolve(undefined);
      } else {
        resolve(reader.result);
      }
    };
    reader.readAsDataURL(blob);
  });
};

export const contactName = (contact: Parameters<typeof contactNames>[0]): string => contactNames(contact).join(' ');
export const teamUserName = (teammate: { userTeamName: string | null; userEmail: string }) =>
  teammate.userTeamName ? firstNameLastInitial(teammate.userTeamName) : teammate.userEmail;

export const firstNameLastInitial = (name: string) => {
  const splitNames = name.split(' ');
  return [splitNames[0], splitNames[1]?.slice(0, 1)].filter(isNotNull).join(' ');
};

export type DataField = {
  data: string;
};

export const contactNames = (contact: {
  id: string;
  primaryFields: { firstName?: DataField; lastName?: DataField; nameFull?: DataField };
}) => {
  const firstName = contact.primaryFields.firstName?.data;
  const lastName = contact.primaryFields.lastName?.data;
  const nameFull = contact.primaryFields.nameFull?.data;

  return (isNotEmpty(firstName) && isNotEmpty(lastName) ? [firstName, lastName] : [nameFull]).filter(isNotEmpty);
};

export const contactNameV2 = (contact: {
  id: string;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  nameFull?: string | null | undefined;
}): string => contactNamesV2(contact).join(' ');
export const contactNamesV2 = (contact: {
  id: string;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  nameFull?: string | null | undefined;
}) => {
  const firstName = contact.firstName;
  const lastName = contact.lastName;
  const nameFull = contact.nameFull;

  return (isNotEmpty(firstName) && isNotEmpty(lastName) ? [firstName, lastName] : [nameFull]).filter(isNotEmpty);
};

/**
 * Determines if an honorific should be displayed based on:
 * - If it's "Dr." - always show regardless of age
 * - For other honorifics (Mr., Ms., Mrs.) - only show if age meets threshold
 */
export const shouldShowHonorific = (
  honorificTitle: string | null | undefined,
  ageEstimate: { minAge: number } | null | undefined,
  honorificAgeThreshold: number | null | undefined,
): boolean => {
  if (!honorificTitle) return false;

  // Always show Dr. regardless of age
  const isDrHonorific = honorificTitle.toLowerCase().startsWith('dr');
  if (isDrHonorific) return true;

  // For other honorifics, check age threshold
  return honorificAgeThreshold != null && ageEstimate?.minAge != null && ageEstimate.minAge >= honorificAgeThreshold;
};

export const getTextFromHtml = (html: string, insertSpacesBetweenPTags?: boolean) => {
  if (html === '<p><br></p>') return '';
  if (html === '<div><br></div>') return '';
  let newHtml = html;
  const domParser = new DOMParser();
  if (insertSpacesBetweenPTags) {
    const document = domParser.parseFromString(newHtml, 'text/html');
    const tags = document.querySelectorAll('p');
    tags.forEach((el) => {
      if (!el.innerHTML.endsWith(' ')) {
        el.innerHTML += ' ';
      }
    });
    newHtml = document.body.innerHTML;
  }
  newHtml = newHtml?.replace(/<br\s*\/?>/gm, '\n');
  const document = domParser.parseFromString(newHtml, 'text/html');
  return document.body.textContent?.replace(/[^\x20-\x7E]+/g, '') || '';
};

const JOIN_ID_SEPARATOR = '.';
export type SplitIds = {
  contactId?: string;
  cadenceId?: string;
  aiStepId?: string;
  reminderId?: string;
  interactionId?: string;
  userId?: string;
  teamId?: string;
};

export const joinIds = ({ cadenceId, contactId, aiStepId, reminderId, interactionId, userId, teamId }: SplitIds) =>
  // position 3 was legacy one-off stepId — kept empty for URL compatibility
  [contactId, cadenceId, '', aiStepId, reminderId, interactionId, userId, teamId]
    .map((a) => a || '')
    .join(JOIN_ID_SEPARATOR);

export function splitIds(id: string): SplitIds {
  // position 3 was legacy one-off stepId — skipped (destructuring hole) for URL compatibility
  const [contactId, cadenceId, , aiStepId, reminderId, interactionId, userId, teamId] = id.split(JOIN_ID_SEPARATOR);
  return {
    contactId: isNotEmpty(contactId) ? contactId : undefined,
    cadenceId: isNotEmpty(cadenceId) ? cadenceId : undefined,
    aiStepId: isNotEmpty(aiStepId) ? aiStepId : undefined,
    reminderId: isNotEmpty(reminderId) ? reminderId : undefined,
    interactionId: isNotEmpty(interactionId) ? interactionId : undefined,
    userId: isNotEmpty(userId) ? userId : undefined,
    teamId: isNotEmpty(teamId) ? teamId : undefined,
  };
}

export function idsMatch(id1: string | SplitIds | undefined, id2: string | SplitIds | undefined) {
  const id1Split = typeof id1 === 'string' ? splitIds(id1) : id1;
  const id2Split = typeof id2 === 'string' ? splitIds(id2) : id2;

  if (id1Split == null && id2Split == null) return true;
  if (id1Split == null || id2Split == null) return false;

  if (id1Split.cadenceId != id2Split.cadenceId) return false;
  if (id1Split.contactId != id2Split.contactId) return false;
  if (id1Split.userId != id2Split.userId) return false;
  if (id1Split.teamId != id2Split.teamId) return false;

  return true;
}

export function idsIncluded(id: string | SplitIds | undefined, ids: string[] | undefined) {
  if (ids == null) return false;
  return ids.find((i) => idsMatch(i, id)) != null;
}

export const calculateNewSIds = (
  selectedId: string,
  _selectedIds: string[] | undefined,
  allIds: string[],
  e: React.MouseEvent,
) => {
  const selectedIds = _selectedIds?.map((id) => decodeURIComponent(id));
  if (e.shiftKey && (selectedIds?.length ?? 0) > 0) {
    const firstSId = selectedIds?.[0];
    const aIndex = allIds?.findIndex((id) => id === firstSId);
    const bIndex = allIds?.findIndex((id) => id === selectedId);

    const startIndex = min([aIndex, bIndex]);
    const endIndex = max([aIndex, bIndex]);
    if (startIndex != null && endIndex != null) {
      return allIds?.slice(startIndex, endIndex + 1).map((id) => id);
    }
  } else {
    return selectedIds?.includes(selectedId)
      ? selectedIds.filter((item) => item !== selectedId)
      : [...(selectedIds || []), selectedId];
  }
};

export function calculateNewSIdsBy<O extends object>(
  selectedId: string,
  sId: string[] | undefined,
  objs: O[] | undefined,
  idFn: (o: O) => string,
  e: React.MouseEvent,
) {
  if (e.shiftKey && (sId?.length ?? 0) > 0) {
    const firstSId = sId?.[0];
    const aIndex = objs?.findIndex((c) => idFn(c) === firstSId);
    const bIndex = objs?.findIndex((c) => idFn(c) === selectedId);

    const startIndex = min([aIndex, bIndex]);
    const endIndex = max([aIndex, bIndex]);
    if (startIndex != null && endIndex != null) {
      return objs?.slice(startIndex, endIndex + 1).map((c) => idFn(c));
    }
  } else {
    return sId?.includes(selectedId) ? sId.filter((item) => item !== selectedId) : [...(sId || []), selectedId];
  }
}

export const getPrettyAmount = (amount: number) => {
  let prettyAmount = amount;
  let amountBadge = '';
  if (amount > 999999) {
    prettyAmount = amount / 1000000;
    amountBadge = 'mln';
  } else if (amount > 999) {
    prettyAmount = amount / 1000;
    amountBadge = 'k';
  }
  prettyAmount = Number(prettyAmount.toFixed(1));
  return `${prettyAmount}${amountBadge}`;
};
export const getCapitalizedAllString = (string: string) => {
  const prettyStringArr = string.toLowerCase().split('_');
  const prettyString = prettyStringArr.map((str) => str.charAt(0).toUpperCase() + str.slice(1)).join(' ');
  return prettyString;
};

// contact: Contact
export const convertHardcodedHtmlToTemplate = (html: string) => {
  if (!html) return html;

  // this is a huge problem if someone's name is "John E". Because it will replace all E's and e's in the text as @LastName
  // For example "Hey John E. Nice to meet you." -> "Hey @FirstName @LastName. Nic@LastName to m@LastName@LastNamet you."
  // So this will be commented out until we find a better solution
  // const firstName = ContactUtil.extractFieldValue(contact, 'firstName')
  // const lastName = ContactUtil.extractFieldValue(contact, 'lastName')
  // const companyFullName = ContactUtil.extractFieldValue(contact, 'companyFull');

  const spanRegex = /(<span[^>]*>.*?<\/span>)/g;
  const htmlParts = html.split(spanRegex) || [];

  return htmlParts.join('');
};

export const isValidUrl = (urlString: string) => {
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const isValidEmail = (testString: string | undefined) => {
  return Boolean(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.exec(
      String(testString).toLowerCase(),
    ),
  );
};

export const getInteractionStepType = (action: ContactInteractionAction): SvrHistoryStepType => {
  switch (action) {
    case 'connection_accepted':
      return 'connectAccept';
    case 'connection_sent':
      return 'connect';

    case 'email_sent':
      return 'mail';

    case 'inmail_sent':
      return 'inmail';

    case 'inmail_received':
      return 'inmail';

    case 'message_sent':
      return 'message';

    case 'message_received':
      return 'message';

    case 'email_received':
      return 'mailReplied';

    case 'cadence_start':
      return 'startedCadence';
    case 'campaign_start':
      return 'startedCadence';

    case 'document_viewed':
      return 'document';

    case 'phone_received':
      return 'call';
    case 'phone_call_sent':
      return 'call';
    case 'comment_on_post':
      return 'comment';

    case 'inmail_declined':
      return 'declinedInmail';

    case 'stage_changed':
      return 'stageChanged';

    case 'follow':
      return 'follow';
    case 'like_posts':
      return 'postsLike';
    case 'reminder_completed':
      return 'reminder';
    case 'visit':
      return 'visit';
    case 'campaign_delete':
      return 'campaignDelete';

    case 'step_failed':
    case 'step_skipped':
    case 'step_canceled':
      return 'error';

    case 'meeting_booked':
      return 'meetingBooked';

    case 'contact_unavailable':
      return 'contactUnavailable';

    case 'contact_available':
      return 'contactAvailable';

    case 'cadence_state_changed':
      return 'cadenceStateChanged';

    default: {
      // this will never be called, but forcing
      // the variable to be of type 'never' will make the typechecker
      // complain if all the cases are not handled
      const nonExhaustive: never = action;
      // have a console error only to silence the linter
      // about unused variables
      throw new Error('non exhaustive match', nonExhaustive);
    }
  }
};

export const getDateDifference = (firstDate: Date, secondDate: Date) => {
  const date1 = new Date(firstDate);
  const date2 = new Date(secondDate);
  const timeDiff = Math.abs(date2.getTime() - date1.getTime());
  const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));
  return diffDays;
};

// export const getStepData = (
//   step: SvrCadenceAddStepParams,
//   teamId: string,
//   contactId: string,
// ): SvrStepsCreate | undefined => {
//   switch (step.type) {
//     case 'inmail':
//       return {
//         contactId,
//         planRunAt: new Date(),
//         body: step.body2,
//         subject: step.subject,
//         type: step.type,
//       };
//     case 'email':
//       return {
//         contactId,
//         planRunAt: new Date(),
//         body: step.body2,
//         subject: step.subject,
//         type: step.type,
//         useEmailSignature: step.useEmailSignature,
//       };
//     case 'message':
//       return { contactId, planRunAt: new Date(), body: step.body2, type: step.type };
//     case 'connect':
//       return { contactId, planRunAt: new Date(), body: step.body2, type: step.type };
//     default:
//       return undefined;
//   }
// };

export function isCurrentTimeInBusinessHours(timezone: string) {
  const now = DateTime.now().setZone(timezone);
  const hour = now.hour;
  const isBusinessHours = hour >= 9 && hour <= 18;
  return isBusinessHours;
}

export function extractRoleAndCompany(text: string): [string | undefined, string | undefined] {
  // Remove single quotes if present
  text = text.replace(/'/g, '');

  let parts: string[] = [];
  // Check if the text contains '@' or 'at' to split role and company
  if (text.includes('@')) {
    parts = text.split('@');
  } else if (text.includes(' at ')) {
    parts = text.split(' at ');
  } else {
    parts = [];
  }

  const jobRole = parts[0]?.split('|').pop()?.trim();
  const companyName = parts[1]?.split('|')?.[0]?.trim();

  return [jobRole, companyName];
}

export const clearSavedUserFilter = () => {
  localStorage.removeItem('savedUserFilter');
};

export const temlateStepTypeToHuman = (stepType: SvrCadenceStep['type']) => {
  switch (stepType) {
    case 'message':
      return 'LinkedIn Message';
    case 'inmail':
      return 'LinkedIn InMail';
    case 'connect':
      return 'LinkedIn Connect';
    case 'visit':
      return 'LinkedIn Visit';
    case 'reminder':
      return 'Manual';
    case 'call':
      return 'Call';
    case 'email':
      return 'Email';
    case 'delay':
      return 'Delay';
    case 'follow':
      return 'Follow';
    case 'wait_for_connect':
      return 'Wait for connect';
    case 'likeRecentPosts':
      return 'Like recent posts';
    case 'voice_note':
      return 'Send Voice note';
    case 'sift':
      return 'Filter';
    case 'switch_campaign':
      return 'Change to campaign';
    case 'switch':
      return 'Switch';
    default: {
      const nonExhaustive: never = stepType;
      console.error('non exhaustive match', nonExhaustive);
      throw new Error('non exhaustive match');
    }
  }
};

export const isNotEmptyTipTap = (s: string | undefined | null) => isNotEmpty(s) && s != '<p></p>';

/**
 * <p></p> is actually empty text
 * @param text input text
 * @returns
 */
export const cleanText = (text: string | undefined): string | undefined => {
  if (text == null) return undefined;
  if (text === '<p></p>' || text.length === 0) return '';
  return text;
};

/** runs a loading/success/error toast */
export const toastTriggerAction =
  (loadingMessage: string, successMessage: string | React.JSX.Element) => async (fn: () => Promise<unknown>) => {
    try {
      await toast.promise(fn(), {
        loading: loadingMessage,
        success: successMessage,
        error: (error: unknown) => {
          if (error instanceof TRPCClientError) {
            return error.message;
          }
          return 'Failed to perform action';
        },
      });
    } catch {
      // noop -> we are showing error message above
    }
  };

type ReplaceHtmlError = [string, boolean];

export const convertTemplateToHtml = (
  html: string,
  atVariables?: Record<string, string>,
  documents?: EditorDocument[],
  hideError?: boolean,
): ReplaceHtmlError => {
  const div = document.createElement('div');
  div.innerHTML = html;

  let hasError = false; // check has forbidden to send variables

  const confirmError = () => {
    if (!hideError) {
      hasError = true;
    }
  };

  div.querySelectorAll('[data-id]').forEach((d) => {
    const id = d.getAttribute('data-id');
    if (id == null) {
      // unhandled variables
      return;
    }

    let type: string | undefined = undefined; // should be equal to name of mention extension on tiptap
    let value: string | undefined = undefined;
    let fileUrl: string | undefined = undefined;
    let imageUrl: string | undefined = undefined;

    const startValue = d.getAttribute('data-label');
    const document = documents?.find((doc) => doc.id === id);
    // TipTap stores variables with @ prefix (e.g., "@FirstName") but atVariables uses keys without @ (e.g., "FirstName")
    const variableKey = id.startsWith('@') ? id.slice(1) : id;
    const atVariable = atVariables?.[variableKey];
    const isComment = id === '{Comment}';
    const isDocumentType = id.startsWith('doc_');

    if (atVariable) {
      type = 'atVariables'; // defined atVariables
      value = atVariable;
    } else if (document != null && !('isPrivate' in document)) {
      type = 'documents'; // defined documents
      value = `${document.title}`;
      fileUrl = document.fileUrl;
      imageUrl = documentThumbnailUrl(document);
    } else if (document != null && 'isPrivate' in document) {
      type = 'documents'; // defined documents, but private
      value = '{private_content}';
      confirmError();
    } else if (isComment) {
      type = 'comments'; // all the comments
      value = startValue ?? '{Comment}';
      confirmError();
    } else if (isDocumentType) {
      type = 'documents'; // undefined documents
      value = '{document_not_found}';
      confirmError();
    } else {
      type = 'atVariables'; // undefined atVariables
      value = `{${id}}`;
      confirmError();
    }

    d.innerHTML = value;
    d.setAttribute('data-type', type);
    d.setAttribute('data-label', value);
    if (fileUrl) {
      d.setAttribute('data-file-url', fileUrl);
    } else {
      d.removeAttribute('data-file-url');
    }
    if (imageUrl) {
      d.setAttribute('data-image-url', imageUrl);
    } else {
      d.removeAttribute('data-image-url');
    }
  });
  return [div.innerHTML, hasError];
};

export const fakeAtVariables: Record<string, string> = {
  FirstName: '{FirstName}',
  LastName: '{LastName}',
  Title: '{Title}',
  Company: '{Company}',
  City: '{City}',
};

/**
 * Replace `{Var}` / `{{Var}}` placeholders in plain text with their resolved
 * values from an atVariables dict (e.g. `FirstName` -> "Bob"). Handles both the
 * canonical single-brace form the editor inserts and the double-brace form some
 * templates contain, plus surrounding whitespace (`{{ FirstName }}`). Unknown
 * keys are left untouched so the user can see what didn't resolve.
 */
export const resolveAtVariables = (text: string, atVariables: Record<string, string>): string =>
  text.replace(/\{\{?\s*([^{}]+?)\s*\}\}?/g, (match, varName: string) => {
    const val = atVariables[varName];
    return val != null ? val : match;
  });

export const aiFakeAtVariables: Record<string, string> = {
  FirstName: '{FirstName}',
  LastName: '{LastName}',
  Title: '{Title}',
  Company: '{Company}',
  City: '{City}',
  Headline: '{Headline}',
  About: '{About}',
};

export const messageReceivedFromExtensionSchema = z
  .object({
    type: z.literal('RUN_CADENCES_STARTED'),
    data: z.object({ tabId: z.number().optional(), nextCadenceIds: z.array(z.string()).optional() }).optional(),
  })
  .or(z.object({ type: z.literal('RUN_CADENCES_STOPPED') }))
  .or(
    z.object({
      type: z.literal('RUN_STEPS_STARTED'),
      data: z.object({ tabId: z.number().optional() }).optional(),
    }),
  )
  .or(z.object({ type: z.literal('CONNECT_AWK') }))
  .or(z.object({ type: z.literal('CHECK_MESSAGES') }))
  .or(z.object({ type: z.literal('CHECK_SALES_NAV_MESSAGES') }))
  .or(z.object({ type: z.literal('CHECK_CONNECTIONS') }))
  .or(z.object({ type: z.literal('CHECK_SENT_INVITATIONS') }))
  .or(z.object({ type: z.literal('RUN_STEP_FINISHED') }))
  .or(z.object({ type: z.literal('RUN_CADENCE_FINISHED') }))
  .or(
    z.object({
      type: z.literal('RUN_CADENCE'),
      data: z.object({
        id: z.string(),
        explicitCadenceIds: z.array(z.string()).optional(),
      }),
    }),
  )
  .or(
    z.object({
      type: z.literal('RUN_STEP'),
      data: z.object({
        id: z.string(),
      }),
    }),
  )
  .or(
    z.object({
      type: z.literal('ERROR'),
      data: z.object({
        action: z.string(),
        code: z.string(),
        contactId: z.string(),
        message: z.string(),
      }),
    }),
  );

export type MessageReceivedFromExtension = z.infer<typeof messageReceivedFromExtensionSchema>;

export type ToastProps<T> = {
  loading: string | React.JSX.Element;
  /** Static message, or derive it from the mutation result (e.g. "3 staged, 1 previously rejected"). */
  success: string | React.JSX.Element | ((data: T | undefined) => string | React.JSX.Element);
  error?: string | React.JSX.Element;
  mutationFnAsync?: () => Promise<T>;
  onAfterSuccess?: (data?: T) => void;
  onError?: (error: string) => void;
  setQuery?: SetSearchFn;
  setLoading?: React.Dispatch<React.SetStateAction<string | undefined>>;
  onSuccessQuery?: SearchParams;
  onErrorQuery?: SearchParams;
};
export async function updateWithToast<T>(props: ToastProps<T>) {
  const toastId = toast.loading(props.loading);
  const loadingText = typeof props.loading === 'string' ? props.loading : undefined;
  props.setLoading?.(loadingText);
  try {
    const data = props.mutationFnAsync ? await props.mutationFnAsync() : undefined;
    props.setQuery?.((q) => ({ ...q, ...props.onSuccessQuery }));
    props.setLoading?.(undefined);
    toast.success(typeof props.success === 'function' ? props.success(data) : props.success, { id: toastId });
    if (props.onAfterSuccess) props.onAfterSuccess(data as T);
  } catch (e) {
    props.setQuery?.((q) => ({ ...q, ...props.onErrorQuery }));
    props.setLoading?.(undefined);
    if (e instanceof Error) {
      toast.error(props.error || e.message, { id: toastId });
      if (props.onError) props.onError(e.message);
    }
  }
}

/**
 * Turns a residential-proxy reconnect failure into an accurate toast message for the
 * enable/disable actions. The server borrows a shared Unipile error classifier that promises
 * "Will retry automatically." — true for cadence execution (which has a retry queue), but
 * FALSE for these one-shot manual reconnects: reconnectLinkedInAccount does not retry, so
 * nothing happens later. Rewrite the message to tell the user to try again themselves and
 * never imply an auto-retry, and make clear nothing changed.
 */
export function proxyReconnectError(serverError: string | undefined, action: 'enable' | 'disable'): string {
  if (serverError && /rate.?limit/i.test(serverError)) {
    return 'LinkedIn is limiting reconnects right now — nothing was changed. Wait a minute and try again.';
  }
  const cleaned = serverError?.replace(/\s*Will retry automatically\.?/i, '').trim();
  return cleaned && cleaned.length > 0
    ? `${cleaned} Nothing was changed — try again.`
    : `Could not turn this ${action === 'enable' ? 'on' : 'off'} — nothing was changed. Please try again.`;
}

// import { type AppRouter } from '@/server/api/root';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isTRPCClientError(cause: unknown): cause is TRPCClientError<any> {
  // export function isTRPCClientError(cause: unknown): cause is TRPCClientError<AppRouter> {
  return cause instanceof TRPCClientError;
}

/**
 * generates a retry function for react query to only retry on 5xx error codes
 *
 * @param retryCount (number) - how many retries to do
 * @returns
 */

export function getReactQueryRetryFn(retryCount: number) {
  return (failureCount: number, error: unknown) => {
    if (isTRPCClientError(error) && failureCount <= retryCount) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return error.data != null && error.data.httpStatus >= 500;
    }
    // some other error, do not retry
    return false;
  };
}

export function sleepAsync(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export type ToastPropsPromise<T> = {
  loading: string | React.JSX.Element;
  /** Static message, or derive it from the mutation result (e.g. "3 staged, 1 previously rejected"). */
  success: string | React.JSX.Element | ((data: T) => string | React.JSX.Element);
  error?: string | React.JSX.Element;
  mutationFnAsync: () => Promise<T>;
};

export async function updateWithToastPromise<T>(props: ToastPropsPromise<T>): Promise<T> {
  const toastId = toast.loading(props.loading);
  try {
    const data = await props.mutationFnAsync();
    toast.success(typeof props.success === 'function' ? props.success(data) : props.success, { id: toastId });
    return data;
  } catch (e) {
    if (e instanceof Error) {
      toast.error(props.error || e.message, { id: toastId });
    }
    throw e; // Re-throw the error so the promise rejects
  }
}

export const timeOptions = [
  { value: '0', label: 'Midnight' },
  { value: '1', label: '01:00 AM' },
  { value: '2', label: '02:00 AM' },
  { value: '3', label: '03:00 AM' },
  { value: '4', label: '04:00 AM' },
  { value: '5', label: '05:00 AM' },
  { value: '6', label: '06:00 AM' },
  { value: '7', label: '07:00 AM' },
  { value: '8', label: '08:00 AM' },
  { value: '9', label: '09:00 AM' },
  { value: '10', label: '10:00 AM' },
  { value: '11', label: '11:00 AM' },
  { value: '12', label: 'Noon' },
  { value: '13', label: '01:00 PM' },
  { value: '14', label: '02:00 PM' },
  { value: '15', label: '03:00 PM' },
  { value: '16', label: '04:00 PM' },
  { value: '17', label: '05:00 PM' },
  { value: '18', label: '06:00 PM' },
  { value: '19', label: '07:00 PM' },
  { value: '20', label: '08:00 PM' },
  { value: '21', label: '09:00 PM' },
  { value: '22', label: '10:00 PM' },
  { value: '23', label: '11:00 PM' },
];

export function oauthUrl(type: 'slack' | 'slack-team' | 'slack-superadmin') {
  const redirectOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  // const redirectOrigin = 'https://9d174527d2b4.ngrok-free.app';
  switch (type) {
    case 'slack':
      return redirectOrigin + '/oauth/slack';
    case 'slack-team':
      return redirectOrigin + '/oauth/slack-team';
    case 'slack-superadmin':
      return redirectOrigin + '/oauth/slack-superadmin';
    default:
      return redirectOrigin + '/oauth/slack';
  }
}

export function getInitials(name?: string | null) {
  return (
    name
      ?.split(' ')
      .map((n) => n[0])
      .filter(isNotEmpty)
      .join('')
      .toUpperCase() ?? ''
  );
}

export function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
